import { usePostTrackEventWithParams } from "./analyticsQueries"

export type TPortfolioAnalyticsEventTypes =
  | "PortfolioAnalytics Homegroup Filters Engaged"
  | "PortfolioAnalytics View Details Clicked"
  | "PortfolioAnalytics Read More Clicked"
  | "PortfolioAnalytics Upgrade Plan Clicked"
  | "PortfolioAnalytics Go To Integration Clicked"
  | "PortfolioAnalytics Drilldown Completed"

function usePostPortfolioAnalyticsTrackingEvent<TEventProperties>(
  event: TPortfolioAnalyticsEventTypes
) {
  return usePostTrackEventWithParams<
    TPortfolioAnalyticsEventTypes,
    TEventProperties
  >({
    event,
  })
}

/**
 * Tracks engagement with homegroup filters in PortfolioAnalytics.
 * @returns Mutation for tracking the event.
 */
export function usePostPortfolioAnalyticsHomegroupFiltersEngaged() {
  return usePostPortfolioAnalyticsTrackingEvent<{
    homeGroupId?: string
  }>("PortfolioAnalytics Homegroup Filters Engaged")
}

/**
 * Properties for the View Details Clicked event.
 */
export type TPortfolioAnalyticsViewDetailsClickedProperties = {
  context: "wifi_coverage" | "uptime"
}
/**
 * Tracks when view details is clicked in PortfolioAnalytics.
 * @returns Mutation for tracking the event.
 */
export function usePostPortfolioAnalyticsViewDetailsClicked() {
  return usePostPortfolioAnalyticsTrackingEvent<TPortfolioAnalyticsViewDetailsClickedProperties>(
    "PortfolioAnalytics View Details Clicked"
  )
}

/**
 * Properties for the Read More Clicked event.
 */
export type TPortfolioAnalyticsReadMoreClickedProperties = {
  context: "portfolio_insights"
}
/**
 * Tracks when read more is clicked in PortfolioAnalytics.
 * @returns Mutation for tracking the event.
 */
export function usePostPortfolioAnalyticsReadMoreClicked() {
  return usePostPortfolioAnalyticsTrackingEvent<TPortfolioAnalyticsReadMoreClickedProperties>(
    "PortfolioAnalytics Read More Clicked"
  )
}

/**
 * Properties for the Upgrade Plan Clicked event.
 */
export type TPortfolioAnalyticsUpgradePlanClickedProperties = {
  context: "portfolio_insights"
}
/**
 * Tracks when upgrade plan is clicked in PortfolioAnalytics.
 * @returns Mutation for tracking the event.
 */
export function usePostPortfolioAnalyticsUpgradePlanClicked() {
  return usePostPortfolioAnalyticsTrackingEvent<TPortfolioAnalyticsUpgradePlanClickedProperties>(
    "PortfolioAnalytics Upgrade Plan Clicked"
  )
}

/**
 * Properties for the Go To Integration Clicked event.
 */
export type TPortfolioAnalyticsGoToIntegrationClickedProperties = {
  context: "portfolio_insights"
  integrationId?: string
}
/**
 * Tracks when go to integration is clicked in PortfolioAnalytics.
 * @returns Mutation for tracking the event.
 */
export function usePostPortfolioAnalyticsGoToIntegrationClicked() {
  return usePostPortfolioAnalyticsTrackingEvent<TPortfolioAnalyticsGoToIntegrationClickedProperties>(
    "PortfolioAnalytics Go To Integration Clicked"
  )
}

/**
 * Tracks when a drilldown is completed in PortfolioAnalytics.
 * @returns Mutation for tracking the event.
 */
export function usePostPortfolioAnalyticsDrilldownCompleted() {
  return usePostPortfolioAnalyticsTrackingEvent<Record<string, never>>(
    "PortfolioAnalytics Drilldown Completed"
  )
}
